import React, { useState } from 'react';
import { FRIDGE_PROGRAM, IMG } from '../mock';
import { Button } from './ui/button';
import { Check, Refrigerator, ArrowUpRight, Leaf } from 'lucide-react';
import FridgeLeadDialog from './FridgeLeadDialog';

const TIER_ID = { Starter: 'starter', Team: 'team', HQ: 'hq' };

export default function FridgeProgram() {
  const { tag, headline, intro, benefits, tiers, provisioning } = FRIDGE_PROGRAM;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState('starter');

  const openTier = (name) => {
    setSelectedTier(TIER_ID[name] || 'starter');
    setDialogOpen(true);
  };
  return (
    <section id="fridge" className="bg-[#1B4631] text-[#F1EDDF] relative overflow-hidden">
      <div className="absolute -top-32 -right-24 w-[440px] h-[440px] rounded-full bg-[#245A40] opacity-60 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-24 w-[420px] h-[420px] rounded-full bg-[#3E7A5A] opacity-50 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#245A40] text-[#E8C56B] text-xs font-semibold border border-[#3E7A5A]">
              <Refrigerator className="w-3.5 h-3.5" /> {tag}
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[64px] leading-[1.04] mt-5">
              {headline.before}
              <em className="italic text-[#E8C56B]">{headline.emphasis}</em>
              {headline.after}
            </h2>
            <p className="mt-6 text-[#D7DBC8] text-lg leading-relaxed max-w-xl">{intro}</p>

            <div className="mt-10 grid sm:grid-cols-2 gap-3">
              {benefits.map((b) => (
                <div key={b.title} className="bg-[#245A40] rounded-2xl p-5 border border-[#3E7A5A]/60">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-[#1B4631] grid place-items-center">
                      <Leaf className="w-4 h-4 text-[#E8C56B]" />
                    </div>
                    <div className="font-display text-lg text-[#F6F2E8]">{b.title}</div>
                  </div>
                  <p className="mt-2.5 text-[#D7DBC8] text-sm leading-relaxed">{b.body}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="rounded-[28px] overflow-hidden border-4 border-[#245A40] grain">
              <img src={IMG.spread} alt="Wholesome 360 office fridge program — healthy office lunches stocked for Chicago teams" className="w-full h-[360px] sm:h-[460px] object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-4 bg-[#F6F2E8] text-[#1B2A22] rounded-2xl shadow-2xl p-4 w-60 border border-[#D6CFB8] rotate-[-3deg]">
              <div className="flex items-center gap-2">
                <Refrigerator className="w-5 h-5 text-[#245A40]" />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#6F6A55]">Restocked weekly</div>
                  <div className="text-sm font-semibold">2–3x per week</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Provisioning callout — what W360 provides */}
        {provisioning && (
          <div className="mt-14 bg-[#F1E2B6] text-[#1B2A22] rounded-3xl p-7 sm:p-9 border border-[#E8C56B] shadow-2xl grid md:grid-cols-12 gap-6 items-start">
            <div className="md:col-span-4 flex items-start gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[#245A40] grid place-items-center shrink-0">
                <Refrigerator className="w-6 h-6 text-[#E8C56B]" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#245A40] font-bold">What we handle</div>
                <div className="font-display text-2xl sm:text-[28px] leading-tight mt-1">{provisioning.title}</div>
              </div>
            </div>
            <ul className="md:col-span-8 grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {provisioning.lines.map((l, i) => (
                <li key={i} className="flex items-start gap-2 text-sm sm:text-[15px] leading-relaxed">
                  <Check className="w-4 h-4 mt-0.5 shrink-0 text-[#245A40]" />
                  <span>{l}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Tiers */}
        <div className="mt-20 grid md:grid-cols-3 gap-5">
          {tiers.map((t) => (
            <div
              key={t.name}
              className={`rounded-3xl p-7 sm:p-8 border ${
                t.featured
                  ? 'bg-[#F1E2B6] text-[#1B2A22] border-[#E8C56B] shadow-2xl scale-[1.02]'
                  : 'bg-[#245A40] text-[#F1EDDF] border-[#3E7A5A]/60'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="font-display text-2xl">{t.name}</div>
                {t.featured && (
                  <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 bg-[#245A40] text-[#E8C56B] rounded-full">Most popular</span>
                )}
              </div>
              <div className="text-sm mt-1 opacity-80">{t.size} employees • {t.perWeek}</div>
              <div className="flex items-baseline gap-1 mt-6">
                <div className="font-display text-5xl">{t.price}</div>
                <div className="text-sm opacity-75">{t.frequency}</div>
              </div>
              <div className="mt-6 space-y-2.5">
                {t.includes.map((inc) => (
                  <div key={inc} className="flex items-start gap-2 text-sm">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${t.featured ? 'text-[#245A40]' : 'text-[#E8C56B]'}`} />
                    <span>{inc}</span>
                  </div>
                ))}
              </div>
              <Button
                onClick={() => openTier(t.name)}
                className={`w-full mt-7 rounded-full h-11 font-semibold ${
                  t.featured
                    ? 'bg-[#245A40] hover:bg-[#1B4631] text-[#F6F2E8]'
                    : 'bg-[#F6F2E8] hover:bg-[#ECE7D6] text-[#1B2A22]'
                }`}
              >
                {t.price === 'Custom' ? 'Request quote' : 'Start program'} <ArrowUpRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      <FridgeLeadDialog open={dialogOpen} onOpenChange={setDialogOpen} tier={selectedTier} />
    </section>
  );
}
